/**
 * SA-1329: the triage block's pure rules: which of three states a message's
 * block is in, what its receipt chip says, and what a waiting row still needs
 * chosen before it can be approved.
 */
import type { TriageKindOption, TriageRow } from '@/types/inbox'
import { OUTCOME_WORDS } from './triageText'

export type BlockState = 'waiting' | 'failed' | 'decided'

export function blockState(row: TriageRow): BlockState {
  if (row.decisionStatus === 'proposed') return 'waiting'
  return row.handlerOutcome === 'failed' ? 'failed' : 'decided'
}

export interface ReceiptChip {
  label: string
  variant: 'success-soft' | 'neutral' | 'destructive-soft'
  tip: string | null
}

export function receiptChip(row: TriageRow): ReceiptChip {
  if (row.handlerOutcome === 'rejected') return { label: 'Not an enquiry', variant: 'neutral', tip: null }
  if (row.handlerOutcome === 'failed') {
    return { label: 'Failed', variant: 'destructive-soft', tip: row.errorWords ?? OUTCOME_WORDS.failed }
  }
  if (row.kind === 'internal') return { label: 'Dismissed', variant: 'neutral', tip: null }
  return { label: row.kindLabel ?? row.kind ?? 'Unknown', variant: 'success-soft', tip: null }
}

export type NeedsChoice = 'record' | 'outcome' | null

export function kindOption(kinds: readonly TriageKindOption[], id: string | null | undefined): TriageKindOption | undefined {
  return id ? kinds.find((option) => option.id === id) : undefined
}

/** What a WAITING row needs picked before Approve: the record it is about, or
 * the outcome a closing kind proposes. */
export function needsChoice(row: TriageRow, kinds: readonly TriageKindOption[]): NeedsChoice {
  if (row.decisionStatus !== 'proposed') return null
  const option = kindOption(kinds, row.proposedKind)
  if (row.match) return 'record'
  if (option?.takesTarget && row.targetNeeded?.[option.outcomes?.length ? 'close' : 'update']) return 'record'
  if (option?.outcomes?.length && !row.outcome) return 'outcome'
  return null
}
